import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { API_BASE } from '../../config'
import { SafeImage } from '../../components/ui/SafeImage'

const topics = [
  'Lead generation systems',
  'Meta Ads strategies',
  'Sales conversion frameworks',
  'Digital growth for high-value industries',
]

interface InsightPreview {
  title: string
  slug: string
  summary?: string
  cover?: string
}

export function InsightsPreview() {
  const [insights, setInsights] = useState<InsightPreview[]>([])

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/insights`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.insights)) return
        setInsights(data.insights.slice(0, 3))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const displayItems = insights.length > 0 ? insights : [
    { title: 'How to Structure Meta Ads for Quality Leads', slug: 'meta-ads-quality-leads', summary: undefined, cover: undefined },
    { title: 'The BLS Method: Why Order Matters', slug: 'bls-method-order', summary: undefined, cover: undefined },
    { title: 'From Enquiry to Customer: Sales Process Design', slug: 'sales-process-design', summary: undefined, cover: undefined },
  ]

  return (
    <section className="section-pad px-4 sm:px-6 lg:px-8 bg-white dark:bg-navy-900">
      <div className="container-wide">
        <motion.h2
          className="section-heading text-center mb-4"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Knowledge & Insights
        </motion.h2>
        <motion.p
          className="section-sub text-center max-w-2xl mx-auto mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.08 }}
        >
          We regularly share insights on:
        </motion.p>
        <motion.ul
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 sm:mb-14"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {topics.map((t) => (
            <li
              key={t}
              className="px-3 sm:px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs sm:text-sm text-slate-700 dark:text-slate-300"
            >
              {t}
            </li>
          ))}
        </motion.ul>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {displayItems.map((item, index) => (
            <motion.div
              key={item.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 + index * 0.08, duration: 0.5 }}
              whileHover={{ y: -6 }}
            >
              <Link
                to={insights.length > 0 ? `/insights/${item.slug}` : '/insights'}
                className="card-glass block p-0 overflow-hidden rounded-card hover:shadow-card-hover dark:hover:shadow-card-hover transition-all duration-300 group"
              >
                <div className="aspect-video overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <SafeImage
                    src={item.cover}
                    alt=""
                    label={item.title}
                    tone="document"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100 group-hover:text-primary dark:group-hover:text-accent transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  {item.summary && <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{item.summary}</p>}
                  <span className="mt-3 inline-flex items-center gap-2 text-sm text-primary dark:text-accent font-medium">
                    Read more <ArrowRight size={14} strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <motion.div
          className="mt-8 sm:mt-10 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Link to="/insights" className="btn-secondary">
            View all insights
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
